import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import { useNotifications } from '../context/NotificationContext';
import {
  Bell,
  BookOpen,
  CheckCircle,
  Circle,
  RefreshCw,
  CheckSquare,
  Square,
  Clock,
  FileText,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  CheckCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';

const Notificaciones = () => {
  const navigate = useNavigate();
  const { resetCount, decrementCount, refreshCount } = useNotifications();
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0
  });

  const loadNotificaciones = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await api.get(`/notificaciones?page=${page}&page_size=${pagination.pageSize}`);
      setNotificaciones(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        page: response.data.page,
        total: response.data.total
      }));
      setSelectedIds([]);
    } catch (error) {
      console.error('Error loading notificaciones:', error);
      toast.error('Error al cargar las notificaciones');
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize]);

  useEffect(() => {
    loadNotificaciones(1);
  }, []);

  const handleMarcarLeida = async (id) => {
    try {
      await api.put(`/notificaciones/${id}/leer`);
      setNotificaciones(prev =>
        prev.map(n => n.id === id ? { ...n, leida: true } : n)
      );
      decrementCount(1);
      toast.success('Notificación marcada como leída');
    } catch (error) {
      toast.error('Error al marcar la notificación');
    }
  };

  const handleMarcarTodasLeidas = async () => {
    try {
      const response = await api.put('/notificaciones/leer-todas');
      setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })));
      resetCount();
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Error al marcar las notificaciones');
    }
  };

  const handleMarcarSeleccionadasLeidas = async () => {
    if (selectedIds.length === 0) {
      toast.warning('Selecciona al menos una notificación');
      return;
    }
    try {
      const response = await api.put('/notificaciones/leer-seleccionadas', { ids: selectedIds });
      setNotificaciones(prev =>
        prev.map(n => selectedIds.includes(n.id) ? { ...n, leida: true } : n)
      );
      decrementCount(selectedIds.length);
      setSelectedIds([]);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Error al marcar las notificaciones');
    }
  };

  const handleSelectAll = () => {
    const noLeidasIds = notificaciones.filter(n => !n.leida).map(n => n.id);
    if (selectedIds.length === noLeidasIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(noLeidasIds);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleNotificacionClick = (notificacion) => {
    // Marcar como leída si no lo está
    if (!notificacion.leida) {
      handleMarcarLeida(notificacion.id);
    }

    // Navegar a la entidad relacionada
    if (notificacion.entidad_tipo === 'requerimiento' && notificacion.entidad_id) {
      navigate('/solicitudes', { state: { highlightId: notificacion.entidad_id } });
    } else if (notificacion.entidad_tipo === 'leccion_aprendida' && notificacion.entidad_id) {
      navigate(`/constructora/calidad/lecciones-aprendidas/${notificacion.entidad_id}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Hace un momento';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `Hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;

    return date.toLocaleDateString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getIconByTipo = (tipo) => {
    switch (tipo) {
      case 'asignacion_requerimiento':
        return <FileText size={20} className="text-blue-500" />;
      case 'requerimiento_vencido':
        return <AlertTriangle size={20} className="text-amber-500" />;
      case 'nueva_leccion_aprendida':
        return <BookOpen size={20} className="text-[#9d8272]" />;
      default:
        return <Bell size={20} className="text-gray-500" />;
    }
  };

  const getBadgeByTipo = (tipo) => {
    switch (tipo) {
      case 'asignacion_requerimiento':
        return <Badge className="bg-blue-100 text-blue-800">Asignación</Badge>;
      case 'requerimiento_vencido':
        return <Badge className="bg-amber-100 text-amber-800">Vencido</Badge>;
      case 'nueva_leccion_aprendida':
        return <Badge className="bg-[#fbf7f0] text-[#9d8272] border border-[#e8dfd0]">Lección</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Info</Badge>;
    }
  };

  const noLeidasCount = notificaciones.filter(n => !n.leida).length;
  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="notificaciones-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#2D2D2D] flex items-center gap-2">
            <Bell className="text-[#9d8272]" />
            Notificaciones
          </h1>
          <p className="text-sm text-[#888888] mt-1">
            {pagination.total} notificaciones • {noLeidasCount} sin leer
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedIds.length > 0 && (
            <Button
              size="sm"
              onClick={handleMarcarSeleccionadasLeidas}
              className="bg-[#9d8272] hover:bg-[#8a7264]"
            >
              <CheckSquare size={16} className="mr-2" />
              Marcar seleccionadas ({selectedIds.length})
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarcarTodasLeidas}
            disabled={noLeidasCount === 0}
          >
            <CheckCheck size={16} className="mr-2" />
            Marcar todas
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadNotificaciones(pagination.page)}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* Select All Checkbox */}
      {noLeidasCount > 0 && (
        <div className="flex items-center gap-2 px-2">
          <Checkbox
            checked={selectedIds.length === noLeidasCount && noLeidasCount > 0}
            onCheckedChange={handleSelectAll}
            id="select-all"
          />
          <label htmlFor="select-all" className="text-sm text-[#5A5A5A] cursor-pointer">
            Seleccionar todas las no leídas
          </label>
        </div>
      )}

      {/* Notificaciones List */}
      <Card className="border-[#E8E8E7]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <RefreshCw size={32} className="animate-spin text-[#9d8272] mx-auto mb-3" />
                <p className="text-sm text-[#888888]">Cargando notificaciones...</p>
              </div>
            </div>
          ) : notificaciones.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Bell size={48} className="text-[#E8E8E7] mb-3" />
              <p className="text-[#888888]">No tienes notificaciones</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E8E8E7]">
              {notificaciones.map((notificacion) => (
                <div
                  key={notificacion.id}
                  className={`p-4 hover:bg-[#F9F9F9] transition-colors ${
                    !notificacion.leida ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox para no leídas */}
                    {!notificacion.leida && (
                      <div className="pt-1">
                        <Checkbox
                          checked={selectedIds.includes(notificacion.id)}
                          onCheckedChange={() => handleSelectOne(notificacion.id)}
                        />
                      </div>
                    )}
                    
                    {/* Indicador de leída/no leída */}
                    {notificacion.leida && (
                      <div className="pt-1">
                        <CheckCircle size={18} className="text-green-500" />
                      </div>
                    )}

                    {/* Icono de tipo */}
                    <div className="pt-0.5">
                      {getIconByTipo(notificacion.tipo)}
                    </div>

                    {/* Contenido */}
                    <div 
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => handleNotificacionClick(notificacion)}
                    >
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`font-medium ${!notificacion.leida ? 'text-[#2D2D2D]' : 'text-[#5A5A5A]'}`}>
                          {notificacion.titulo}
                        </span>
                        {getBadgeByTipo(notificacion.tipo)}
                        {notificacion.entidad_codigo && (
                          <Badge variant="outline" className="font-mono text-xs">
                            {notificacion.entidad_codigo}
                          </Badge>
                        )}
                      </div>
                      <p className={`text-sm ${!notificacion.leida ? 'text-[#5A5A5A]' : 'text-[#888888]'}`}>
                        {notificacion.mensaje}
                      </p>
                      <div className="flex items-center gap-1 mt-2 text-xs text-[#AAAAAA]">
                        <Clock size={12} />
                        {formatDate(notificacion.creado_en)}
                      </div>
                    </div>

                    {/* Acción rápida */}
                    {!notificacion.leida && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarcarLeida(notificacion.id);
                        }}
                        className="text-[#888888] hover:text-[#9d8272]"
                        title="Marcar como leída"
                      >
                        <Circle size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {notificaciones.length > 0 && (
            <div className="flex items-center justify-between p-4 border-t border-[#E8E8E7]">
              <p className="text-sm text-[#888888]">
                Página {pagination.page} de {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadNotificaciones(pagination.page - 1)}
                  disabled={pagination.page <= 1 || loading}
                >
                  <ChevronLeft size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadNotificaciones(pagination.page + 1)}
                  disabled={pagination.page >= totalPages || loading}
                >
                  <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Notificaciones;
